// The shared-playlist landing page (#325): what a phone opens from a shared
// playlist's link or QR code.
//
// The page itself is deliberately almost nothing — it resolves the link and
// gets out of the way. Signed in with access, it opens the playlist into your
// own list, LINKED (src/data/sharedPlaylistSync.ts: edits go both ways, if the
// link allows editing), and lands on that playlist's page. The set strip,
// auto-advance and dead-ref reporting all work unchanged. Anything short of
// access shows the sign-in flow INLINE rather than an error: the link is often
// someone's first contact with the app, and "sign in, then this opens" has to
// be one screen, not a treasure hunt through the nav drawer.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { APP_ROUTES } from '@/navigation';
import { useAuth } from '@/auth/session';
import { openSharedPlaylistLink } from '@/data/sharedPlaylistSync';
import { recordFeature } from '@/analytics/usage';
import { AccountSection } from '@/components/AccountSection';

export function SharedSetlistPage() {
  const { shareId } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Bumped by Retry so the effect re-runs after a transport failure.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (auth.status !== 'active' || !shareId) return;
    let cancelled = false;
    setError(null);
    void openSharedPlaylistLink(shareId)
      .then((playlistId) => {
        if (cancelled) return;
        if (!playlistId) {
          setError('That link did not open a playlist. Ask for a fresh link.');
          return;
        }
        recordFeature('setlist-open');
        navigate(`${APP_ROUTES.playlists}/${playlistId}`, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setError('Could not reach the playlist. Try again in a moment.');
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, shareId, attempt, navigate]);

  return (
    <div className="playlists-page shared-setlist-page" data-testid="shared-setlist-page">
      <h1>Shared playlist</h1>

      {auth.status === 'unconfigured' && (
        <p className="muted" data-testid="shared-setlist-unavailable">
          Shared playlists are not available in this build.{' '}
          <Link to={APP_ROUTES.playlists}>Back to playlists</Link>.
        </p>
      )}

      {auth.status === 'loading' && <p className="muted">Opening…</p>}

      {(auth.status === 'signedOut' ||
        auth.status === 'linkSent' ||
        auth.status === 'needsCode') && (
        <div className="shared-setlist-signin" data-testid="shared-setlist-signin">
          <p className="muted">Sign in and the playlist opens here.</p>
          <AccountSection />
        </div>
      )}

      {auth.status === 'active' &&
        (error ? (
          <>
            <p className="home-load-error" role="alert" data-testid="shared-setlist-error">
              {error}
            </p>
            <button
              type="button"
              className="btn btn-sm"
              data-testid="shared-setlist-retry"
              onClick={() => setAttempt((n) => n + 1)}
            >
              Try again
            </button>
          </>
        ) : (
          <p className="muted">Opening the playlist…</p>
        ))}
    </div>
  );
}
